import { RatingSummary } from "../../components/StarRating";
import PhoneLink from "../../components/PhoneLink";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  deleteTransportMaster,
  fetchTransportMasterTrips,
  listTransportMaster,
  saveTransportMaster,
  uploadTransportDocument,
} from "../../../services/transportMasterApi";
import { listAdminLeadLanguages } from "../../../services/leadTrackerApi";
import DriverLoansSection from "./DriverLoansSection";
import { formatDateDMY, formatDateTimeDMY } from "../../../utils/dateFormat";

const blankOwner = {
  name: "", phone: "", email: "", baseCity: "", languages: [], address: "", upiNumber: "", upiScannerUrl: "",
  bankAccountHolder: "", bankAccountNumber: "", bankIfsc: "", bankName: "", bankBranch: "", active: true,
};
const blankVehicleModel = { name: "", category: "", seater: 4, active: true };
const blankVehicle = {
  vehicleModelId: "", registrationNumber: "", ac: true,
  ownerType: "", ownerId: "", active: true,
  imageUrls: [], documents: [],
};
const blankDriver = {
  name: "", phone: "", affiliationType: "INDIVIDUAL", ownerId: "", languages: [], licenseNumber: "",
  address: "", upiNumber: "", upiScannerUrl: "", bankAccountHolder: "", bankAccountNumber: "", bankIfsc: "",
  bankName: "", bankBranch: "", active: true, documents: [],
};
const vehicleForOwner = (ownerId) => ({ ...blankVehicle, ownerType: "OWNER", ownerId });
const driverForOwner = (ownerId) => ({ ...blankDriver, affiliationType: "OWNER_DRIVER", ownerId });
const MODAL_LABELS = { owners: "Owner", vehicles: "Vehicle", drivers: "Driver", "vehicle-models": "Vehicle Model" };

export default function TransportManager() {
  const [owners, setOwners] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerTripsById, setOwnerTripsById] = useState({});
  const [formModal, setFormModal] = useState(null);
  const [modalSaving, setModalSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ownerRows, vehicleRows, driverRows, modelRows] = await Promise.all([
        listTransportMaster("owners"),
        listTransportMaster("vehicles"),
        listTransportMaster("drivers"),
        listTransportMaster("vehicle-models"),
      ]);
      setVehicleModels(modelRows || []);
      setOwners(ownerRows || []);
      setVehicles(vehicleRows || []);
      setDrivers(driverRows || []);
      setOwnerTripsById({});
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load Transport Master");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadOwnerTrips = async (ownerId) => {
    if (ownerTripsById[ownerId]) return;
    setOwnerTripsById((current) => ({ ...current, [ownerId]: "loading" }));
    try {
      const data = await fetchTransportMasterTrips("owners", ownerId);
      setOwnerTripsById((current) => ({ ...current, [ownerId]: data }));
    } catch (error) {
      setOwnerTripsById((current) => ({ ...current, [ownerId]: "error" }));
      toast.error(error?.response?.data?.message || "Could not load owner trips");
    }
  };

  const fleetGroups = useMemo(() => {
    const vehiclesByOwnerId = groupBy(vehicles, "ownerId");
    const employedDriversByOwnerId = groupBy(
      drivers.filter((driver) => driver.affiliationType === "OWNER_DRIVER"), "ownerId");
    const registeredFleets = owners.map((owner) => ({
      owner,
      vehicles: vehiclesByOwnerId[owner.id] || [],
      drivers: employedDriversByOwnerId[owner.id] || [],
    }));
    const soloDrivers = drivers.filter((driver) => driver.affiliationType === "INDIVIDUAL");
    // A vehicle still linked via the deprecated individual-driver-owner path (legacy data) has no
    // registered owner either, so it belongs here too rather than being invisible in the Fleets view.
    const orphanVehicles = vehicles.filter((vehicle) => !vehicle.ownerId);
    return { registeredFleets, soloDrivers, orphanVehicles };
  }, [owners, vehicles, drivers]);

  const openModal = (type, value) => setFormModal({ type, value: clone(value) });
  const openNewOwner = () => openModal("owners", blankOwner);
  const openEditOwner = (owner) => openModal("owners", owner);
  const openNewVehicleFor = (owner) => openModal("vehicles", vehicleForOwner(owner.id));
  const openNewOrphanVehicle = () => openModal("vehicles", blankVehicle);
  const openEditVehicle = (vehicle) => openModal("vehicles", vehicle);
  const openNewDriverFor = (owner) => openModal("drivers", driverForOwner(owner.id));
  const openNewIndividualDriver = () => openModal("drivers", blankDriver);
  const openEditDriver = (driver) => openModal("drivers", driver);
  const closeModal = () => { if (!modalSaving) setFormModal(null); };

  const saveModal = async () => {
    const { type, value } = formModal;
    setModalSaving(true);
    try {
      await saveTransportMaster(type, normalize(value));
      toast.success(`${MODAL_LABELS[type]} saved`);
      setFormModal(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Could not save ${MODAL_LABELS[type].toLowerCase()}`);
    } finally {
      setModalSaving(false);
    }
  };

  const removeEntity = async (type, row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try {
      await deleteTransportMaster(type, row.id);
      toast.success(`${MODAL_LABELS[type]} deleted`);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || `Could not delete ${MODAL_LABELS[type].toLowerCase()}`);
    }
  };

  if (loading) return <Card body className="text-center py-5"><Spinner /></Card>;

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">Transport Master</div>
        <div className="small text-muted">Onboard vehicles, drivers, owners, bank details, and documents.</div>
      </Card.Header>
      <Card.Body>
        <FleetsView
          groups={fleetGroups}
          ownerTripsById={ownerTripsById}
          onExpandOwnerTrips={loadOwnerTrips}
          onAddOwner={openNewOwner}
          onEditOwner={openEditOwner}
          onAddVehicleFor={openNewVehicleFor}
          onAddDriverFor={openNewDriverFor}
          onAddIndividualDriver={openNewIndividualDriver}
          onAddOrphanVehicle={openNewOrphanVehicle}
          onEditVehicle={openEditVehicle}
          onEditDriver={openEditDriver}
          onRemove={removeEntity}
        />
        <hr className="my-4" />
        <DriverLoansSection drivers={drivers} />
        <hr className="my-4" />
        <VehicleModelsSection
          models={vehicleModels}
          onNew={() => openModal("vehicle-models", blankVehicleModel)}
          onEdit={(model) => openModal("vehicle-models", model)}
          onDelete={(model) => removeEntity("vehicle-models", model)}
        />
      </Card.Body>

      {formModal && (
        <MasterFormModal
          type={formModal.type}
          label={MODAL_LABELS[formModal.type]}
          value={formModal.value}
          setValue={(updater) => setFormModal((current) => ({ ...current, value: typeof updater === "function" ? updater(current.value) : updater }))}
          onSave={saveModal}
          onHide={closeModal}
          saving={modalSaving}
          owners={owners}
          drivers={drivers}
          vehicleModels={vehicleModels}
        />
      )}
    </Card>
  );
}

function VehicleModelsSection({ models, onNew, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div role="button" className="d-flex align-items-center gap-2" onClick={() => setOpen((v) => !v)}>
          <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
          <div>
            <div className="fw-semibold">Vehicle Models ({models.length})</div>
            <div className="small text-muted">The list vehicles pick from. Keeping one row per real model is what stops duplicate spellings.</div>
          </div>
        </div>
        <Button size="sm" variant="outline-secondary" onClick={onNew}>+ Vehicle Model</Button>
      </div>
      {open && (
        <div className="table-responsive mt-3">
          <Table size="sm" hover className="small mb-0 align-middle bg-white">
            <thead>
              <tr><th style={{ width: 44 }}>#</th><th>Model</th><th>Category</th><th>Seater</th><th>Vehicles</th><th>Status</th><th className="text-end">Actions</th></tr>
            </thead>
            <tbody>
              {models.map((model, index) => (
                <tr key={model.id}><td className="small fw-semibold">{index + 1}</td>
                  <td className="fw-semibold">{model.name}</td>
                  <td>{model.category}</td>
                  <td>{model.seater}</td>
                  <td>{model.vehicleCount || 0}</td>
                  <td><Badge bg={model.active === false ? "secondary" : "success"}>{model.active === false ? "Inactive" : "Active"}</Badge></td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-2" onClick={() => onEdit(model)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(model)} disabled={!!model.vehicleCount}
                            title={model.vehicleCount ? "Move its vehicles to another model first" : ""}>Delete</Button>
                  </td>
                </tr>
              ))}
              {!models.length && <tr><td colSpan={7} className="text-center text-muted py-3">No vehicle models yet.</td></tr>}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

function MasterFormModal({ type, label, value, setValue, onSave, onHide, saving, owners, drivers, vehicleModels }) {
  const FormView = { owners: OwnerForm, vehicles: VehicleForm, drivers: DriverForm, "vehicle-models": VehicleModelForm }[type];
  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{value.id ? `Edit ${label}` : `New ${label}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormView value={value} setValue={setValue} owners={owners} drivers={drivers} vehicleModels={vehicleModels} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button onClick={onSave} disabled={saving}>{saving ? "Saving..." : `Save ${label}`}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function FleetsView({
  groups, ownerTripsById, onExpandOwnerTrips,
  onAddOwner, onEditOwner, onAddVehicleFor, onAddDriverFor, onAddIndividualDriver,
  onAddOrphanVehicle, onEditVehicle, onEditDriver, onRemove,
}) {
  const { registeredFleets, soloDrivers, orphanVehicles } = groups;
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const matches = useCallback(
    (...values) => values.some((v) => v && String(v).toLowerCase().includes(query)),
    [query]);

  const filteredFleets = useMemo(() => {
    if (!query) return registeredFleets;
    return registeredFleets.filter((group) =>
      matches(group.owner.name, group.owner.phone)
      || group.vehicles.some((v) => matches(v.name, v.registrationNumber))
      || group.drivers.some((d) => matches(d.name, d.phone)));
  }, [registeredFleets, query, matches]);

  const filteredSoloDrivers = useMemo(
    () => (query ? soloDrivers.filter((d) => matches(d.name, d.phone)) : soloDrivers),
    [soloDrivers, query, matches]);

  const filteredOrphanVehicles = useMemo(
    () => (query ? orphanVehicles.filter((v) => matches(v.name, v.registrationNumber)) : orphanVehicles),
    [orphanVehicles, query, matches]);

  return (
    <div className="pt-3 d-flex flex-column gap-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex flex-wrap gap-2">
          <Button size="sm" onClick={onAddOwner}>+ Add Owner</Button>
          <Button size="sm" variant="outline-secondary" onClick={onAddIndividualDriver}>+ Add Freelance Driver</Button>
          <Button size="sm" variant="outline-secondary" onClick={onAddOrphanVehicle}>+ Add Vehicle</Button>
        </div>
        <Form.Control
          size="sm"
          style={{ maxWidth: 320 }}
          placeholder="Search owner, vehicle, or driver"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div>
        <div className="fw-semibold mb-2">Registered Owners ({filteredFleets.length})</div>
        {!filteredFleets.length && <div className="small text-muted">{query ? "No registered owners match your search." : "No registered owners yet."}</div>}
        <div className="d-flex flex-column gap-2">
          {filteredFleets.map((group) => (
            <FleetCard
              key={`owner-${group.owner.id}`}
              header={group.owner}
              vehicles={group.vehicles}
              drivers={group.drivers}
              tripsData={ownerTripsById[group.owner.id]}
              onExpandTrips={() => onExpandOwnerTrips(group.owner.id)}
              onEdit={() => onEditOwner(group.owner)}
              onDelete={() => onRemove("owners", group.owner)}
              onAddVehicle={() => onAddVehicleFor(group.owner)}
              onAddDriver={() => onAddDriverFor(group.owner)}
              onCopyOwnerPortal={() => copyText(group.owner.ownerPortalUrl, "Owner portal link copied")}
              onEditVehicle={onEditVehicle}
              onDeleteVehicle={(vehicle) => onRemove("vehicles", vehicle)}
              onEditDriver={onEditDriver}
              onDeleteDriver={(driver) => onRemove("drivers", driver)}
            />
          ))}
        </div>
      </div>

      <div>
        <div className="fw-semibold mb-2">Freelance Drivers ({filteredSoloDrivers.length})</div>
        {!filteredSoloDrivers.length && <div className="small text-muted">{query ? "No freelance drivers match your search." : "No freelance drivers without a vehicle."}</div>}
        {!!filteredSoloDrivers.length && (
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0 align-middle bg-white">
              <thead>
                <tr><th style={{ width: 44 }}>#</th><th>Name</th><th>Phone</th><th>Languages</th><th>License</th><th>Portal Link</th><th>Trips</th><th>Rating</th><th>Status</th><th className="text-end">Actions</th></tr>
              </thead>
              <tbody>
                {filteredSoloDrivers.map((driver, index) => (
                  <tr key={driver.id}><td className="small fw-semibold">{index + 1}</td>
                    <td>{driver.name}</td>
                    <td>{driver.phone}</td>
                    <td className="small">{(driver.languages || []).join(", ") || "-"}</td>
                    <td>{driver.licenseNumber || "-"}</td>
                    <td>{driver.driverPortalUrl ? <Button size="sm" variant="outline-primary" onClick={() => copyText(driver.driverPortalUrl)}>Copy Link</Button> : "-"}</td>
                    <td><TripCountBadges row={driver} /></td>
                    <td><RatingSummary average={driver.averageRating} ratedTrips={driver.ratedTripCount} /></td>
                    <td>{driver.active ? "Active" : "Inactive"}</td>
                    <td className="text-end text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => onEditDriver(driver)}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => onRemove("drivers", driver)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <div>
        <div className="fw-semibold mb-2">Vehicles Needing an Owner ({filteredOrphanVehicles.length})</div>
        {!filteredOrphanVehicles.length && <div className="small text-muted">{query ? "No unowned vehicles match your search." : "Every vehicle has an owner assigned."}</div>}
        {!!filteredOrphanVehicles.length && (
          <Alert variant="warning" className="mb-0 p-0">
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0 align-middle bg-white">
                <thead>
                  <tr><th style={{ width: 44 }}>#</th><th>Name</th><th>Registration</th><th>Category</th><th>Seater</th><th>AC</th><th>Rating</th><th>Status</th><th className="text-end">Actions</th></tr>
                </thead>
                <tbody>
                  {filteredOrphanVehicles.map((vehicle, index) => (
                    <tr key={vehicle.id}><td className="small fw-semibold">{index + 1}</td>
                      <td>{vehicle.name}</td>
                      <td>{vehicle.registrationNumber}</td>
                      <td>{vehicle.category}</td>
                      <td>{vehicle.seater}</td>
                      <td>{vehicle.ac ? "AC" : "Non AC"}</td>
                      <td><RatingSummary average={vehicle.averageRating} ratedTrips={vehicle.ratedTripCount} /></td>
                      <td>{vehicle.active ? "Active" : "Inactive"}</td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => onEditVehicle(vehicle)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => onRemove("vehicles", vehicle)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Alert>
        )}
      </div>
    </div>
  );
}

function FleetCard({
  header, vehicles, drivers, tripsData, onExpandTrips,
  onEdit, onDelete, onAddVehicle, onAddDriver, onCopyOwnerPortal, onEditVehicle, onDeleteVehicle, onEditDriver, onDeleteDriver,
}) {
  const [expanded, setExpanded] = useState(false);
  // Distinct vehicle names this owner runs, with how many of each, ordered smallest seater first
  // so the fleet reads as a capacity ladder. Same-capacity models fall back to alphabetical.
  const vehicleNameCounts = useMemo(() => {
    const byName = new Map();
    (vehicles || []).forEach((vehicle) => {
      const name = String(vehicle?.name || "").trim();
      if (!name) return;
      const entry = byName.get(name) || { count: 0, seater: Number(vehicle?.seater) || 0 };
      entry.count += 1;
      byName.set(name, entry);
    });
    return Array.from(byName.entries())
      .sort(([leftName, left], [rightName, right]) =>
        left.seater - right.seater || leftName.localeCompare(rightName))
      .map(([name, entry]) => [name, entry.count]);
  }, [vehicles]);
  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) onExpandTrips?.();
  };
  return (
    <Card>
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2" role="button" onClick={toggle}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <i className={`bi ${expanded ? "bi-chevron-down" : "bi-chevron-right"}`} aria-hidden="true" />
          <div>
            <div className="fw-semibold">{header.name}</div>
            <div className="small text-muted d-flex flex-wrap align-items-center gap-1">
              {/* Stops the call icon's click from also expanding or collapsing the card. */}
              <span onClick={(event) => event.stopPropagation()}>
                <PhoneLink phone={header.phone} className="fw-semibold text-body" empty={null} />
              </span>
              {header.baseCity ? <span>&middot; {header.baseCity}</span> : null}
              {header.languages?.length ? <span>&middot; {header.languages.join(", ")}</span> : null}
            </div>
            {vehicleNameCounts.length ? (
              <div className="small text-muted">
                {vehicleNameCounts.map(([name, count]) => `${name} (${count})`).join(", ")}
              </div>
            ) : null}
          </div>
          <TripCountBadges row={header} />
        </div>
        <div className="d-flex flex-wrap gap-2" onClick={(event) => event.stopPropagation()}>
          {header.ownerPortalUrl && <Button size="sm" variant="outline-success" onClick={onCopyOwnerPortal}>Copy Owner Portal</Button>}
          {onAddVehicle && <Button size="sm" variant="outline-secondary" onClick={onAddVehicle}>+ Vehicle</Button>}
          {onAddDriver && <Button size="sm" variant="outline-secondary" onClick={onAddDriver}>+ Driver</Button>}
          <Button size="sm" variant="outline-primary" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="outline-danger" onClick={onDelete}>Delete</Button>
        </div>
      </Card.Header>
      {expanded && (
        <Card.Body>
          <div className="fw-semibold small mb-2">Vehicles ({vehicles.length})</div>
          {!vehicles.length && <div className="small text-muted mb-3">No vehicles yet.</div>}
          {!!vehicles.length && (
            <div className="table-responsive mb-3">
              <Table size="sm" className="small mb-0 align-middle">
                <thead>
                  <tr><th style={{ width: 44 }}>#</th><th>Name</th><th>Registration</th><th>Category</th><th>Seater</th><th>AC</th><th>Rating</th><th>Status</th><th className="text-end">Actions</th></tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle, index) => (
                    <tr key={vehicle.id}><td className="small fw-semibold">{index + 1}</td>
                      <td>{vehicle.name}</td>
                      <td>{vehicle.registrationNumber}</td>
                      <td>{vehicle.category}</td>
                      <td>{vehicle.seater}</td>
                      <td>{vehicle.ac ? "AC" : "Non AC"}</td>
                      <td><RatingSummary average={vehicle.averageRating} ratedTrips={vehicle.ratedTripCount} /></td>
                      <td>{vehicle.active ? "Active" : "Inactive"}</td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => onEditVehicle(vehicle)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => onDeleteVehicle(vehicle)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="fw-semibold small mb-2">Drivers ({drivers.length})</div>
          {!drivers.length && <div className="small text-muted mb-3">No drivers yet.</div>}
          {!!drivers.length && (
            <div className="table-responsive mb-3">
              <Table size="sm" className="small mb-0 align-middle">
                <thead>
                  <tr><th style={{ width: 44 }}>#</th><th>Name</th><th>Phone</th><th>Languages</th><th>License</th><th>Portal Link</th><th>Trips</th><th>Rating</th><th>Status</th><th className="text-end">Actions</th></tr>
                </thead>
                <tbody>
                  {drivers.map((driver, index) => (
                    <tr key={driver.id}><td className="small fw-semibold">{index + 1}</td>
                      <td>{driver.name}</td>
                      <td>{driver.phone}</td>
                      <td className="small">{(driver.languages || []).join(", ") || "-"}</td>
                      <td>{driver.licenseNumber || "-"}</td>
                      <td>{driver.driverPortalUrl ? <Button size="sm" variant="outline-primary" onClick={() => copyText(driver.driverPortalUrl)}>Copy Link</Button> : "-"}</td>
                      <td><TripCountBadges row={driver} /></td>
                      <td><RatingSummary average={driver.averageRating} ratedTrips={driver.ratedTripCount} /></td>
                      <td>{driver.active ? "Active" : "Inactive"}</td>
                      <td className="text-end text-nowrap">
                        <Button size="sm" variant="outline-primary" className="me-2" onClick={() => onEditDriver(driver)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => onDeleteDriver(driver)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <TripsPanel data={tripsData} />
        </Card.Body>
      )}
    </Card>
  );
}

function VehicleForm({ value, setValue, owners, vehicleModels = [] }) {
  const selectedModel = vehicleModels.find((model) => String(model.id) === String(value.vehicleModelId));
  return (
    <>
      <div className="row g-3">
        <VehicleModelSelect value={value.vehicleModelId} models={vehicleModels} onChange={(v) => patch(setValue, "vehicleModelId", v)} />
        <Field label="Registration Number *" value={value.registrationNumber} onChange={(v) => patch(setValue, "registrationNumber", v)} />
        <ReadOnlyField label="Category" value={selectedModel?.category} />
        <ReadOnlyField label="Seater" value={selectedModel?.seater} />
        <SelectField label="AC Type *" value={value.ac ? "AC" : "NON_AC"} onChange={(v) => patch(setValue, "ac", v === "AC")} options={[["AC", "AC"], ["NON_AC", "Non AC"]]} />
        <OwnerSelect value={value.ownerId} owners={owners} onChange={(v) => setValue((old) => ({ ...old, ownerType: v ? "OWNER" : "", ownerId: v }))} optional />
        <Active value={value.active} onChange={(v) => patch(setValue, "active", v)} />
      </div>
      <VehicleImageEditor
        value={value.imageUrls || []}
        onChange={(imageUrls) => patch(setValue, "imageUrls", imageUrls)}
      />
      <DocumentEditor value={value.documents || []} onChange={(documents) => patch(setValue, "documents", documents)} />
    </>
  );
}

function VehicleImageEditor({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (files) => {
    const selected = Array.from(files || []).filter((file) => file.type.startsWith("image/"));
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of selected) uploaded.push(await uploadTransportDocument(file));
      onChange([...value, ...uploaded]);
      toast.success(`${uploaded.length} vehicle image${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch {
      toast.error("Vehicle image upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="border rounded p-3 mt-3">
      <div className="fw-semibold mb-2">Vehicle Images</div>
      <Form.Control
        type="file"
        accept="image/*"
        multiple
        disabled={uploading}
        onChange={(event) => upload(event.target.files)}
      />
      {uploading && <div className="small text-muted mt-2"><Spinner size="sm" className="me-2" />Uploading images...</div>}
      {!!value.length && (
        <div className="d-flex flex-wrap gap-3 mt-3">
          {value.map((url, index) => (
            <div className="border rounded p-2" key={`${url}-${index}`} style={{ width: 150 }}>
              <a href={url} target="_blank" rel="noreferrer">
                <img src={url} alt={`Vehicle ${index + 1}`} className="img-fluid rounded" style={{ height: 90, width: "100%", objectFit: "cover" }} />
              </a>
              <Button size="sm" variant="outline-danger" className="w-100 mt-2" onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DriverForm({ value, setValue, owners }) {
  const individual = value.affiliationType === "INDIVIDUAL";
  return (
    <>
      <div className="row g-3">
        <Field label="Driver Name *" value={value.name} onChange={(v) => patch(setValue, "name", v)} />
        <Field label="Phone Number *" value={value.phone} onChange={(v) => patch(setValue, "phone", v)} />
        <SelectField label="Affiliation *" value={value.affiliationType} onChange={(v) => setValue((old) => ({ ...old, affiliationType: v, ownerId: v === "INDIVIDUAL" ? "" : old.ownerId }))} options={[["INDIVIDUAL", "Individual Driver"], ["OWNER_DRIVER", "Driver of an Owner"]]} />
        {!individual && <OwnerSelect value={value.ownerId} owners={owners} onChange={(v) => patch(setValue, "ownerId", v)} />}
        <LanguagesKnownField value={value.languages || []} onChange={(next) => patch(setValue, "languages", next)} />
        <Field label="Driving License Number" value={value.licenseNumber} onChange={(v) => patch(setValue, "licenseNumber", v)} />
        <Field label="Address" value={value.address} onChange={(v) => patch(setValue, "address", v)} />
        <Active value={value.active} onChange={(v) => patch(setValue, "active", v)} />
      </div>
      <div className="border rounded p-3 mt-3">
        <div className="fw-semibold mb-1">Driver Portal Link</div>
        {value.driverPortalUrl ? (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <a href={value.driverPortalUrl} target="_blank" rel="noreferrer" className="small text-break">{value.driverPortalUrl}</a>
            <Button size="sm" variant="outline-primary" onClick={() => copyText(value.driverPortalUrl)}>Copy</Button>
          </div>
        ) : (
          <div className="small text-muted">Save the driver to generate a unique portal link.</div>
        )}
      </div>
      {individual && <PaymentFields value={value} setValue={setValue} />}
      <DocumentEditor value={value.documents || []} onChange={(documents) => patch(setValue, "documents", documents)} />
    </>
  );
}

function OwnerForm({ value, setValue }) {
  return (
    <>
      <div className="row g-3">
        <Field label="Owner Name *" value={value.name} onChange={(v) => patch(setValue, "name", v)} />
        <Field label="Phone Number *" value={value.phone} onChange={(v) => patch(setValue, "phone", v)} />
        <Field label="Email" value={value.email} onChange={(v) => patch(setValue, "email", v)} />
        <Field label="Base City" value={value.baseCity} onChange={(v) => patch(setValue, "baseCity", v)} placeholder="e.g., Bangalore" />
        <LanguagesKnownField value={value.languages || []} onChange={(next) => patch(setValue, "languages", next)} />
        <Field label="Address" value={value.address} onChange={(v) => patch(setValue, "address", v)} />
        <Active value={value.active} onChange={(v) => patch(setValue, "active", v)} />
      </div>
      <PaymentFields value={value} setValue={setValue} />
    </>
  );
}

function PaymentFields({ value, setValue }) {
  const [uploading, setUploading] = useState(false);
  const uploadScanner = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      patch(setValue, "upiScannerUrl", await uploadTransportDocument(file));
      toast.success("UPI scanner uploaded");
    } catch {
      toast.error("UPI scanner upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <div className="border rounded p-3 mt-3">
      <div className="fw-semibold mb-1">UPI Payment Details</div>
      <div className="small text-muted mb-2">Provide at least one UPI or bank account detail.</div>
      <div className="row g-3">
        <Field label="UPI Number / ID" value={value.upiNumber} onChange={(v) => patch(setValue, "upiNumber", v)} placeholder="Phone number or UPI ID" />
        <div className="col-md-4">
          <Form.Label>UPI Scanner</Form.Label>
          <Form.Control type="file" accept="image/*" disabled={uploading} onChange={(e) => uploadScanner(e.target.files?.[0])} />
          {value.upiScannerUrl && <a href={value.upiScannerUrl} target="_blank" rel="noreferrer" className="small">View uploaded scanner</a>}
        </div>
      </div>
    </div>
    <div className="border rounded p-3 mt-3">
      <div className="fw-semibold mb-1">Bank Account Details <span className="small text-muted">(Optional)</span></div>
      <div className="row g-3">
        <Field label="Account Holder" value={value.bankAccountHolder} onChange={(v) => patch(setValue, "bankAccountHolder", v)} />
        <Field label="Account Number" value={value.bankAccountNumber} onChange={(v) => patch(setValue, "bankAccountNumber", v)} />
        <Field label="IFSC" value={value.bankIfsc} onChange={(v) => patch(setValue, "bankIfsc", v)} />
        <Field label="Bank Name" value={value.bankName} onChange={(v) => patch(setValue, "bankName", v)} />
        <Field label="Branch" value={value.bankBranch} onChange={(v) => patch(setValue, "bankBranch", v)} />
      </div>
    </div>
    </>
  );
}

function DocumentEditor({ value, onChange, requiredTypes = [] }) {
  const [uploading, setUploading] = useState(null);
  const add = () => onChange([...value, { documentType: "OTHER", documentNumber: "", expiryDate: "", documentUrl: "" }]);
  const update = (index, field, next) => onChange(value.map((doc, idx) => idx === index ? { ...doc, [field]: next } : doc));
  const upload = async (index, file) => {
    if (!file) return;
    setUploading(index);
    try {
      update(index, "documentUrl", await uploadTransportDocument(file));
      toast.success("Document uploaded");
    } catch {
      toast.error("Document upload failed");
    } finally {
      setUploading(null);
    }
  };
  return (
    <div className="border rounded p-3 mt-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div><span className="fw-semibold">Documents</span>{requiredTypes.length > 0 && <span className="small text-muted ms-2">RC and Insurance are mandatory</span>}</div>
        <Button size="sm" variant="outline-primary" onClick={add}>Add Other Document</Button>
      </div>
      {value.map((doc, index) => (
        <div className="row g-2 align-items-end border-top py-2" key={index}>
          <SelectField label="Document Type" value={doc.documentType} onChange={(v) => update(index, "documentType", v)} options={[["RC", "RC"], ["INSURANCE", "Insurance"], ["PERMIT", "Permit"], ["POLLUTION", "Pollution Certificate"], ["DRIVING_LICENSE", "Driving License"], ["OTHER", "Other"]]} />
          <Field label="Document Number" value={doc.documentNumber} onChange={(v) => update(index, "documentNumber", v)} />
          <Field label="Expiry Date" type="date" value={doc.expiryDate} onChange={(v) => update(index, "expiryDate", v)} />
          <div className="col-md-3"><Form.Label>Document File</Form.Label><Form.Control type="file" onChange={(e) => upload(index, e.target.files?.[0])} disabled={uploading === index} />{doc.documentUrl && <a href={doc.documentUrl} target="_blank" rel="noreferrer" className="small">View uploaded document</a>}</div>
          <div className="col-md-1"><Button size="sm" variant="outline-danger" disabled={requiredTypes.includes(doc.documentType)} onClick={() => onChange(value.filter((_, idx) => idx !== index))}>Remove</Button></div>
        </div>
      ))}
    </div>
  );
}

function LanguagesKnownField({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    listAdminLeadLanguages()
      .then((rows) => { if (active) setOptions(rows || []); })
      .catch(() => { if (active) setOptions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  const toggle = (name) => onChange(value.includes(name) ? value.filter((item) => item !== name) : [...value, name]);
  return (
    <div className="col-md-12 mb-2">
      <Form.Label>Languages Known</Form.Label>
      <div className="border rounded px-3 py-2">
        {loading && <div className="small text-muted">Loading languages...</div>}
        {!loading && !options.length && (
          <div className="small text-muted">No languages configured. Add them in Lead Tracker &rarr; Languages.</div>
        )}
        {!loading && !!options.length && (
          <div className="d-flex flex-wrap gap-3">
            {options.map((language) => (
              <Form.Check
                key={language.id || language.name}
                type="checkbox"
                id={`driver-language-${language.id || language.name}`}
                label={language.name}
                checked={value.includes(language.name)}
                onChange={() => toggle(language.name)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function VehicleModelSelect({ value, models, onChange }) {
  // Inactive models stay selectable only for vehicles already on them, so retiring a model
  // never blocks editing the vehicles that still use it.
  const options = models.filter((model) => model.active !== false || String(model.id) === String(value));
  return (
    <div className="col-md-3">
      <Form.Label>Vehicle Model *</Form.Label>
      <Form.Select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">{models.length ? "Select model" : "No models configured"}</option>
        {options.map((model) => (
          <option key={model.id} value={model.id}>
            {model.name} ({model.category}, {model.seater})
          </option>
        ))}
      </Form.Select>
      {!models.length && <div className="small text-danger mt-1">Add one in the Vehicle Models section first.</div>}
    </div>
  );
}
function ReadOnlyField({ label, value }) {
  return (
    <div className="col-md-3">
      <Form.Label>{label}</Form.Label>
      <Form.Control value={value ?? ""} readOnly disabled placeholder="From model" />
    </div>
  );
}
function VehicleModelForm({ value, setValue }) {
  return (
    <div className="row g-3">
      <Field label="Model Name *" value={value.name} onChange={(v) => patch(setValue, "name", v)} placeholder="Dzire, Innova Crysta..." />
      <Field label="Category *" value={value.category} onChange={(v) => patch(setValue, "category", v)} placeholder="Sedan, SUV, Tempo Traveller..." />
      <Field label="Seater *" type="number" value={value.seater} onChange={(v) => patch(setValue, "seater", Number(v || 0))} />
      <Active value={value.active} onChange={(v) => patch(setValue, "active", v)} />
    </div>
  );
}
function Field({ label, value, onChange, type = "text", placeholder }) {
  return <div className="col-md-3"><Form.Label>{label}</Form.Label><Form.Control type={type} placeholder={placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value)} /></div>;
}
function SelectField({ label, value, onChange, options }) {
  return <div className="col-md-3"><Form.Label>{label}</Form.Label><Form.Select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>{options.map(([id, name]) => <option value={id} key={id}>{name}</option>)}</Form.Select></div>;
}
function OwnerSelect({ value, owners, onChange, optional = false }) {
  return <div className="col-md-3"><Form.Label>Owner{optional ? "" : " *"}</Form.Label><Form.Select value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}><option value="">{optional ? "No owner assigned" : "Select owner"}</option>{owners.map((owner) => <option value={owner.id} key={owner.id}>{owner.name} - {owner.phone}</option>)}</Form.Select></div>;
}
function Active({ value, onChange }) {
  return <div className="col-md-2 d-flex align-items-end pb-2"><Form.Check type="switch" label="Active" checked={value !== false} onChange={(e) => onChange(e.target.checked)} /></div>;
}
function patch(setValue, field, value) { setValue((old) => ({ ...old, [field]: value })); }
function clone(value) { return JSON.parse(JSON.stringify(value)); }
function copyText(text, successMessage = "Link copied") {
  navigator.clipboard?.writeText(text);
  toast.success(successMessage);
}

function normalize(value) {
  return {
    ...value,
    ownerId: value.ownerId || null,
    imageUrls: value.imageUrls || [],
    documents: value.documents || [],
  };
}

function groupBy(rows, key) {
  const groups = {};
  rows.forEach((row) => {
    const groupKey = row[key];
    if (!groupKey) return;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(row);
  });
  return groups;
}

function TripCountBadges({ row }) {
  // A zero count stays quiet; any non-zero count gets a solid, high-contrast colour so it reads
  // at a glance against the white row.
  const counts = [
    ["ongoing", row.ongoingTripCount, "success"],
    ["upcoming", row.upcomingTripCount, "primary"],
    ["completed", row.completedTripCount, "dark"],
  ];
  return (
    <div className="d-flex gap-1">
      {counts.map(([label, count, tone]) => (
        <Badge
          key={label}
          bg={count ? tone : "light"}
          text={count ? undefined : "secondary"}
          className={count ? undefined : "border"}
        >
          {count || 0} {label}
        </Badge>
      ))}
    </div>
  );
}

function TripsPanel({ data }) {
  if (data === "loading") {
    return <div className="py-2 small text-muted"><Spinner size="sm" className="me-2" />Loading trips...</div>;
  }
  if (!data || data === "error") {
    return <div className="py-2 small text-muted">Could not load trips.</div>;
  }
  const ongoing = data.ongoing || [];
  const upcoming = data.upcoming || [];
  const completed = data.completed || [];
  return (
    <div className="py-2">
      <TripList title="Ongoing" variant="success" rows={ongoing} emptyText="No ongoing trips." />
      <TripList title="Upcoming" variant="primary" rows={upcoming} emptyText="No upcoming trips." className="mt-3" />
      <TripList title="Completed (last 90 days)" variant="secondary" rows={completed} emptyText="No trips completed in the last 90 days." className="mt-3" />
    </div>
  );
}

function TripList({ title, variant, rows, emptyText, className = "" }) {
  return (
    <div className={className}>
      <Badge bg={variant} className="mb-2">{title} ({rows.length})</Badge>
      {!rows.length && <div className="small text-muted">{emptyText}</div>}
      {!!rows.length && (
        <div className="table-responsive">
          <Table size="sm" className="small mb-0 align-middle bg-white">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>Booking</th>
                <th>Trip</th>
                <th>Customer</th>
                <th>Pickup</th>
                <th>Drop</th>
                <th>Vehicle</th>
                <th>Driver</th>
                <th>Payable</th>
                <th>Paid</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((trip, index) => (
                <tr key={trip.bookingId}><td className="small fw-semibold">{index + 1}</td>
                  <td>#{trip.bookingId}</td>
                  <td>
                    <div className="fw-semibold">{trip.tripTitle || "Trip"}</div>
                    {trip.tripDuration && <div className="small text-nowrap">{trip.tripDuration}</div>}
                    {trip.pickupLocation && <div className="text-nowrap"><i className="bi bi-geo-alt me-1" aria-hidden="true" />Pickup: {trip.pickupLocation}</div>}
                    {trip.dropLocation && <div className="text-nowrap"><i className="bi bi-flag me-1" aria-hidden="true" />Drop: {trip.dropLocation}</div>}
                    {trip.vehicleCategory && <div className="text-muted">{trip.vehicleCategory}</div>}
                  </td>
                  <td>
                    <div>{trip.customerName || "-"}</div>
                    {trip.plannedByPhone && <div className="small text-muted">Planned by: {trip.plannedByName || "-"}</div>}
                  </td>
                  <td className="text-nowrap">{trip.pickupDateTime ? formatDateTimeDMY(trip.pickupDateTime) : formatDateDMY(trip.travelDate)}</td>
                  <td className="text-nowrap">{trip.dropDateTime ? formatDateTimeDMY(trip.dropDateTime) : "-"}</td>
                  <td>{trip.vehicleName ? `${trip.vehicleName}${trip.vehicleRegistrationNumber ? ` (${trip.vehicleRegistrationNumber})` : ""}` : "-"}</td>
                  <td>{trip.driverName || "-"}</td>
                  <td className="text-muted text-nowrap">₹{Number(trip.transportPayable || 0).toLocaleString("en-IN")}</td>
                  <td className="text-success text-nowrap">₹{Number(trip.transportPaid || 0).toLocaleString("en-IN")}</td>
                  <td className={`text-nowrap fw-semibold ${Number(trip.transportBalance || 0) > 0 ? "text-danger" : "text-success"}`}>
                    ₹{Number(trip.transportBalance || 0).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}
